
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {

    FileCheck,
    ShieldCheck,
    GraduationCap
} from 'lucide-react';

// Reuse existing components

import DocumentVerification from './onboarding/DocumentVerification';
import BackgroundVerification from './onboarding/BackgroundVerification';
import Orientation from './onboarding/Orientation';

// Detail Components for Drawers

import ViewBackgroundVerification from './onboarding/ViewBackgroundVerification';
import ViewOrientation from './onboarding/ViewOrientation';

import Drawer from '../../components/common/Drawer'; // Assuming generic Drawer exists
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

const OnboardingModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for tabs
    const currentTab = searchParams.get('tab') || 'documentVerification';

    // State for Drawer
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Sync Drawer state with URL action
    useEffect(() => {
        if (action && ['viewBackgroundVerification'].includes(action)) {
            setIsDrawerOpen(true);
        } else {
            setIsDrawerOpen(false);
        }
    }, [action]);

    // Handle Tab Change
    const handleTabChange = (tabKey) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tabKey);
        // Clear action when switching tabs
        newParams.delete('action');
        newParams.delete('id');
        // Clear all filter-related params when switching tabs
        const filterKeys = ['search', 'status', 'documentType', 'vendor', 'type', 'location', 'startDate', 'endDate', 'page', 'limit'];
        filterKeys.forEach(key => newParams.delete(key));
        setSearchParams(newParams);
    };

    // Close Drawer
    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
        setIsDrawerOpen(false);
    };

    // Tabs Configuration
    const tabs = [
        {
            id: 'documentVerification',
            label: 'Document Verification',
            icon: FileCheck,
            component: DocumentVerification,
            permission: 'onboarding'
        },
        {
            id: 'backgroundVerification',
            label: 'Background Check',
            icon: ShieldCheck,
            component: BackgroundVerification,
            permission: ['onboarding', 'self_service']
        },
        {
            id: 'orientation',
            label: 'Orientation',
            icon: GraduationCap,
            component: Orientation,
            permission: 'onboarding'
        }
    ];

    // Render Component for current tab
    const renderContent = () => {
        const activeTabObj = tabs.find(t => t.id === currentTab) || tabs[0];
        const Component = activeTabObj.component;
        return (
            <div className="h-full bg-gray-50">
                <Component />
            </div>
        );
    };

    // Determine Drawer Content based on action
    const renderDrawerContent = () => {
        if (!actionId) return null;

        switch (action) {


            case 'viewBackgroundVerification':
                return <ViewBackgroundVerification id={actionId} />;



            default:
                return null;
        }
    };

    // Get drawer title based on action
    const getDrawerTitle = () => {
        switch (action) {

            case 'viewBackgroundVerification': return 'Background Verification Details';

            default: return 'Details';
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Module Header & Tabs */}
            <div className="bg-white border-b border-gray-200 px-6 pt-6 sticky top-0 z-10 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Onboarding</h1>
                </div>

                {/* Tab Navigation */}
                <div className="flex space-x-8 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => {
                        const isActive = currentTab === tab.id;
                        const Icon = tab.icon;
                        return (
                            <PermissionGate key={tab.id} permission={tab.permission} action="read">
                                <button
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200
                    ${isActive
                                            ? 'border-indigo-500 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                                >
                                    <Icon className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${isActive ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `} />
                                    {tab.label}
                                </button>
                            </PermissionGate>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
                {renderContent()}
            </div>

            {/* Right-side Drawer */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                width="w-full max-w-4xl" // Wider drawer for details
            >
                {isDrawerOpen && renderDrawerContent()}
            </Drawer>
        </div>
    );
};

export default OnboardingModule;
